import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cookie from "lucide-react/dist/esm/icons/cookie";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";
import ChevronUp from "lucide-react/dist/esm/icons/chevron-up";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import {
  CONSENT_OPEN_EVENT,
  CONSENT_STORAGE_KEY,
  CONSENT_TTL_MS,
  CONSENT_VERSION,
  getOrCreateConsentUuid,
  isConsentValid,
  readStoredConsent,
  type StoredConsent,
} from "@/lib/cookieConsent";

interface ConsentState {
  analytics: boolean;
  marketing: boolean;
  external: boolean;
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    analytics: false,
    marketing: false,
    external: false,
  });
  const [prevUuid, setPrevUuid] = useState<string | null>(null);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!isConsentValid(stored)) {
      const timer = setTimeout(() => setVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, []);

  // Persistent re-open (footer link, datenschutz page, etc.)
  useEffect(() => {
    const open = () => {
      const stored = readStoredConsent();
      if (stored) {
        setConsent({
          analytics: !!stored.analytics,
          marketing: !!stored.marketing,
          external: !!stored.external,
        });
        setPrevUuid(stored.uuid);
      }
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener(CONSENT_OPEN_EVENT, open);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, open);
  }, []);

  const saveConsent = useCallback(
    async (analytics: boolean, marketing: boolean, external: boolean) => {
      const uuid = getOrCreateConsentUuid();
      const givenAt = new Date();
      const expiresAt = new Date(givenAt.getTime() + CONSENT_TTL_MS);
      const isRevoke = !analytics && !marketing && !external;
      const eventType: StoredConsent["event_type"] = prevUuid
        ? (isRevoke ? "revoke" : "update")
        : "grant";

      const payload: StoredConsent = {
        uuid,
        version: CONSENT_VERSION,
        given_at: givenAt.toISOString(),
        expires_at: expiresAt.toISOString(),
        analytics,
        marketing,
        external,
        event_type: eventType,
      };
      localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
      setVisible(false);
      setPrevUuid(uuid);

      // Notify other components (e.g. TraumFewoCalendar) immediately
      window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: payload }));

      try {
        await supabase.functions.invoke("log-consent", {
          body: {
            session_id: uuid,
            consent_uuid: uuid,
            consent_version: CONSENT_VERSION,
            event_type: eventType,
            prev_uuid: prevUuid,
            analytics_consent: analytics,
            marketing_consent: marketing,
            external_consent: external,
            consent_given: !isRevoke,
            consented_at: givenAt.toISOString(),
          },
        });
      } catch {
        // Best-effort — consent is already stored locally with HMAC payload on server attempt.
      }
    },
    [prevUuid],
  );

  const acceptAll = () => saveConsent(true, true, true);
  const rejectAll = () => saveConsent(false, false, false);
  const acceptSelection = () => saveConsent(consent.analytics, consent.marketing, consent.external);

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-3 sm:p-4"
        role="dialog"
        aria-label="Cookie-Einstellungen"
      >
        <div className="max-w-2xl mx-auto">
          <div className="rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
            <div className="p-5 sm:p-6 pb-0">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <Cookie className="w-5 h-5 text-foreground" />
                </div>
                <h3 className="font-display text-base font-semibold text-foreground">
                  Datenschutz-Einstellungen
                </h3>
              </div>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">
                Wir verwenden technisch notwendige Speicherung (Sprachwahl, Cookie-Einstellung) auf
                Grundlage von §&nbsp;25 Abs.&nbsp;2 TTDSG. Externe Inhalte
                (Traum-Ferienwohnungen-Kalender) werden ausschließlich nach Ihrer ausdrücklichen
                Einwilligung geladen. Sie können Ihre Auswahl jederzeit über den Link
                „Cookie-Einstellungen" im Footer ändern. Ihre Entscheidung wird pseudonymisiert
                (gekürzte IP, signiert per HMAC) 12&nbsp;Monate gespeichert.{" "}
                <a
                  href="/datenschutzerklaerung"
                  className="underline text-foreground hover:text-accent transition-colors"
                >
                  Datenschutzerklärung
                </a>
              </p>
            </div>

            <div className="px-5 sm:px-6 pt-4 pb-2">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-accent transition-colors mb-3"
                aria-expanded={showDetails}
              >
                {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Cookie-Kategorien {showDetails ? "ausblenden" : "anzeigen"}
              </button>

              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="space-y-2 mb-3"
                >
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex-1 mr-3">
                      <p className="font-medium text-sm text-foreground">Technisch notwendig</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Speichert Sprachwahl (<code>lang</code>), Übersetzungs-Cache
                        (<code>translate_cache_*</code>) und Ihre Cookie-Einstellung
                        (<code>cookie_consent</code>, <code>consent_uuid</code>). Keine Weitergabe
                        an Dritte. Rechtsgrundlage: §&nbsp;25&nbsp;Abs.&nbsp;2 TTDSG /
                        Art.&nbsp;6&nbsp;Abs.&nbsp;1&nbsp;lit.&nbsp;f DSGVO.
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">
                      Immer aktiv
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex-1 mr-3">
                      <p className="font-medium text-sm text-foreground">Statistik</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Aktuell verwenden wir keine Statistik- oder Analyse-Dienste. Diese
                        Kategorie ist als Platzhalter vorhanden, falls zukünftig pseudonyme
                        Reichweitenmessung ergänzt wird.
                      </p>
                    </div>
                    <Switch
                      checked={consent.analytics}
                      onCheckedChange={(v) => setConsent((s) => ({ ...s, analytics: v }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex-1 mr-3">
                      <p className="font-medium text-sm text-foreground">Marketing</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Aktuell werden keine Marketing- oder Trackingdienste eingesetzt. Die
                        Kategorie ist als Platzhalter angelegt.
                      </p>
                    </div>
                    <Switch
                      checked={consent.marketing}
                      onCheckedChange={(v) => setConsent((s) => ({ ...s, marketing: v }))}
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex-1 mr-3">
                      <p className="font-medium text-sm text-foreground">Externe Inhalte</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Verfügbarkeitskalender von <strong>traum-ferienwohnungen.de</strong>
                        {" "}(Traum-Ferienwohnungen GmbH, DE). Beim Laden werden Ihre IP-Adresse
                        und Browser-Daten an den Drittanbieter übermittelt.
                        Rechtsgrundlage: Art.&nbsp;6&nbsp;Abs.&nbsp;1&nbsp;lit.&nbsp;a DSGVO.
                      </p>
                    </div>
                    <Switch
                      checked={consent.external}
                      onCheckedChange={(v) => setConsent((s) => ({ ...s, external: v }))}
                    />
                  </div>
                </motion.div>
              )}
            </div>

            <div className="px-5 sm:px-6 pb-5 sm:pb-6 pt-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  className="flex-1 font-body text-sm h-11 border-foreground/20 text-foreground hover:bg-muted"
                >
                  Alle akzeptieren
                </Button>
                <Button
                  onClick={rejectAll}
                  variant="outline"
                  className="flex-1 font-body text-sm h-11 border-foreground/20 text-foreground hover:bg-muted"
                >
                  Alle ablehnen
                </Button>
              </div>
              {showDetails && (
                <Button
                  onClick={acceptSelection}
                  variant="outline"
                  className="w-full mt-2 font-body text-sm h-11 border-foreground/20 text-foreground hover:bg-muted"
                >
                  Auswahl bestätigen
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
